import { useState, useEffect } from 'react'
import { supabase } from '../supabase'


const FormField = ({ label, children, span }) => (
  <div className="form-group" style={span ? { gridColumn: '1 / -1' } : {}}>
    <label>{label}</label>
    {children}
  </div>
)

function FrameworkLibrary({ session, profile, onNavigate, initialFrameworkId, onOpenCompetitor }) {
  const [frameworks, setFrameworks] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingFramework, setEditingFramework] = useState(null)
  const [selectedFramework, setSelectedFramework] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [frameworkCompetitors, setFrameworkCompetitors] = useState([])

  const [form, setForm] = useState({
    name: '',
    reference_code: '',
    description: '',
    category: '',
    managing_authority: '',
    start_date: '',
    end_date: '',
    tc_url: '',
    notes: '',
    is_active: true
  })

  useEffect(() => {
    loadFrameworks()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (initialFrameworkId && frameworks.length > 0) {
      const fw = frameworks.find(f => f.id === initialFrameworkId)
      if (fw) setSelectedFramework(fw)
    }
  }, [initialFrameworkId, frameworks])

  const loadFrameworks = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('frameworks')
      .select('*')
      .eq('organisation_id', profile?.organisation_id)
      .order('name')
    setFrameworks(data || [])
    setLoading(false)
  }

  const loadFrameworkCompetitors = async (frameworkId) => {
    const { data } = await supabase
      .from('competitor_frameworks')
      .select('id, competitor_id, competitors(id, name, size)')
      .eq('framework_id', frameworkId)
    setFrameworkCompetitors(data || [])
  }

  const resetForm = () => {
    setForm({ name: '', reference_code: '', description: '', category: '', managing_authority: '', start_date: '', end_date: '', tc_url: '', notes: '', is_active: true })
    setShowAdd(false)
    setEditingFramework(null)
    setError(null)
  }

  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    setError(null)

    const payload = {
      name: form.name.trim(),
      reference_code: form.reference_code || null,
      description: form.description || null,
      category: form.category || null,
      managing_authority: form.managing_authority || null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      tc_url: form.tc_url || null,
      notes: form.notes || null,
      is_active: form.is_active
    }

    if (editingFramework) {
      const { error } = await supabase
        .from('frameworks')
        .update(payload)
        .eq('id', editingFramework.id)
      if (error) { setError(error.message); setSaving(false); return }
      const updated = { ...editingFramework, ...payload }
      setFrameworks(prev => prev.map(f => f.id === editingFramework.id ? updated : f))
      if (selectedFramework?.id === editingFramework.id) setSelectedFramework(updated)
    } else {
      const { data, error } = await supabase
        .from('frameworks')
        .insert([{ ...payload, organisation_id: profile?.organisation_id, created_by: session.user.id }])
        .select()
        .single()
      if (error) { setError(error.message); setSaving(false); return }
      setFrameworks(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)))
    }
    setSaving(false)
    resetForm()
  }

  const handleEdit = (fw) => {
    setForm({
      name: fw.name || '',
      reference_code: fw.reference_code || '',
      description: fw.description || '',
      category: fw.category || '',
      managing_authority: fw.managing_authority || '',
      start_date: fw.start_date || '',
      end_date: fw.end_date || '',
      tc_url: fw.tc_url || '',
      notes: fw.notes || '',
      is_active: fw.is_active !== false
    })
    setEditingFramework(fw)
    setShowAdd(true)
    setSelectedFramework(null)
  }

  const handleDelete = async (id) => {
    await supabase.from('frameworks').delete().eq('id', id)
    setFrameworks(prev => prev.filter(f => f.id !== id))
    if (selectedFramework?.id === id) setSelectedFramework(null)
  }

  const handleToggleActive = async (fw) => {
    await supabase.from('frameworks').update({ is_active: !fw.is_active }).eq('id', fw.id)
    setFrameworks(prev => prev.map(f => f.id === fw.id ? { ...f, is_active: !fw.is_active } : f))
    if (selectedFramework?.id === fw.id) setSelectedFramework(prev => ({ ...prev, is_active: !fw.is_active }))
  }


  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>

      {/* Page header */}
      <div className="page-header">
        <p className="label">Reference Library</p>
        <h1>Framework Library</h1>
        <p>Manage the procurement frameworks your organisation operates on. Frameworks link to opportunities and will enable T&amp;C variance analysis when AI is activated.</p>
      </div>

      {error && <div className="error-msg">{error}</div>}

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

        {/* Left — framework list */}
        <div style={{ width: '280px', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-dark)' }}>
              {frameworks.length} Framework{frameworks.length !== 1 ? 's' : ''}
            </div>
            <button
              className="btn btn--primary"
              style={{ padding: '5px 14px', fontSize: '0.8125rem' }}
              onClick={() => { resetForm(); setShowAdd(true); setSelectedFramework(null) }}
            >
              + Add
            </button>
          </div>

          {loading && <p style={{ color: 'var(--slate)', fontSize: '0.875rem' }}>Loading...</p>}

          {!loading && frameworks.length === 0 && (
            <div className="card">
              <p style={{ color: 'var(--slate)', fontSize: '0.875rem', textAlign: 'center', padding: '16px 0' }}>
                No frameworks yet.
              </p>
            </div>
          )}

          {frameworks.map(fw => (
            <div
              key={fw.id}
              onClick={() => { setSelectedFramework(fw); setShowAdd(false); loadFrameworkCompetitors(fw.id) }}
              style={{
                padding: '12px 16px', marginBottom: '4px',
                background: selectedFramework?.id === fw.id ? 'var(--green-pale)' : 'var(--white)',
                border: `1px solid ${selectedFramework?.id === fw.id ? 'var(--green)' : 'var(--green-light)'}`,
                borderLeft: `4px solid ${fw.is_active ? 'var(--green)' : 'var(--silver)'}`,
                cursor: 'pointer', transition: 'all 0.15s'
              }}
              onMouseEnter={e => { if (selectedFramework?.id !== fw.id) e.currentTarget.style.background = 'var(--green-pale)' }}
              onMouseLeave={e => { if (selectedFramework?.id !== fw.id) e.currentTarget.style.background = 'var(--white)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                {fw.reference_code && (
                  <span style={{ fontSize: '0.625rem', fontWeight: 700, color: 'var(--white)', background: 'var(--green-dark)', padding: '1px 6px', letterSpacing: '0.06em' }}>
                    {fw.reference_code}
                  </span>
                )}
                {!fw.is_active && (
                  <span style={{ fontSize: '0.625rem', fontWeight: 600, color: 'var(--slate)', background: 'var(--off-white)', padding: '1px 6px', border: '1px solid var(--silver)' }}>
                    Inactive
                  </span>
                )}
              </div>
              <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--charcoal)' }}>{fw.name}</div>
              {fw.managing_authority && (
                <div style={{ fontSize: '0.75rem', color: 'var(--slate)', marginTop: '2px' }}>{fw.managing_authority}</div>
              )}
            </div>
          ))}
        </div>

        {/* Right — detail or add form */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Add / Edit form */}
          {showAdd && (
            <div className="card">
              <div className="card__header">
                <span className="card__title">{editingFramework ? `Edit — ${editingFramework.name}` : 'New Framework'}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormField label="Framework Name *" span>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Technology Services 4" />
                </FormField>
                <FormField label="Reference Code">
                  <input type="text" value={form.reference_code} onChange={e => setForm({ ...form, reference_code: e.target.value })} placeholder="e.g. TS4, RM6100" />
                </FormField>
                <FormField label="Category">
                  <input type="text" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="e.g. Technology, Professional Services" />
                </FormField>
                <FormField label="Managing Authority">
                  <input type="text" value={form.managing_authority} onChange={e => setForm({ ...form, managing_authority: e.target.value })} placeholder="e.g. Crown Commercial Service, MOD" />
                </FormField>
                <FormField label="Framework Start Date">
                  <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
                </FormField>
                <FormField label="Framework End Date">
                  <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} />
                </FormField>
                <FormField label="T&C URL" span>
                  <input type="text" value={form.tc_url} onChange={e => setForm({ ...form, tc_url: e.target.value })} placeholder="https://www.crowncommercial.gov.uk/agreements/..." />
                </FormField>
                <FormField label="Description" span>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description of the framework scope and purpose" rows={3} />
                </FormField>
                <FormField label="Notes" span>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes — lot structure, approved supplier status, key contacts" rows={3} />
                </FormField>
                <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="checkbox" id="fw-active" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <label htmlFor="fw-active" style={{ cursor: 'pointer', marginBottom: 0, fontWeight: 600 }}>Active framework</label>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button className="btn btn--primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
                  {saving ? 'Saving...' : editingFramework ? 'Save Changes' : 'Add Framework'}
                </button>
                <button className="btn btn--secondary" onClick={resetForm}>Cancel</button>
              </div>
            </div>
          )}

          {/* Framework detail view */}
          {selectedFramework && !showAdd && (
            <div>
              <div style={{ background: 'var(--green-dark)', padding: '20px 24px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '6px' }}>
                      {selectedFramework.managing_authority || 'Framework'}
                    </div>
                    <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.75rem', fontWeight: 600, color: 'var(--white)' }}>
                      {selectedFramework.name}
                    </div>
                    {selectedFramework.reference_code && (
                      <div style={{ fontSize: '0.875rem', color: 'var(--silver)', marginTop: '4px' }}>
                        {selectedFramework.reference_code}
                        {selectedFramework.category && ` · ${selectedFramework.category}`}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      onClick={() => handleEdit(selectedFramework)}
                      style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'var(--silver)', padding: '6px 14px', fontSize: '0.75rem', cursor: 'pointer' }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
                {(selectedFramework.start_date || selectedFramework.end_date) && (
                  <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
                    {selectedFramework.start_date && (
                      <div>
                        <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '2px' }}>Start</div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--white)' }}>
                          {new Date(selectedFramework.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                      </div>
                    )}
                    {selectedFramework.end_date && (
                      <div>
                        <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green-light)', marginBottom: '2px' }}>Expires</div>
                        <div style={{ fontSize: '0.875rem', color: new Date(selectedFramework.end_date) < new Date() ? '#E67E22' : 'var(--white)' }}>
                          {new Date(selectedFramework.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                          {new Date(selectedFramework.end_date) < new Date() && ' — Expired'}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="card" style={{ marginBottom: '16px' }}>
                <div className="card__header">
                  <span className="card__title">Framework Details</span>
                  <span style={{
                    fontSize: '0.625rem', fontWeight: 700,
                    color: selectedFramework.is_active ? 'var(--green)' : 'var(--slate)',
                    background: selectedFramework.is_active ? 'var(--green-pale)' : 'var(--off-white)',
                    padding: '3px 10px', letterSpacing: '0.06em', textTransform: 'uppercase'
                  }}>
                    {selectedFramework.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {selectedFramework.description && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '6px' }}>Description</div>
                    <p style={{ fontSize: '0.9375rem', color: 'var(--charcoal)', lineHeight: 1.6, margin: 0 }}>{selectedFramework.description}</p>
                  </div>
                )}

                {selectedFramework.tc_url && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '6px' }}>Terms &amp; Conditions</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span
                        onClick={() => window.open(selectedFramework.tc_url, '_blank')}
                        style={{ fontSize: '0.875rem', color: 'var(--green)', cursor: 'pointer', textDecoration: 'underline', wordBreak: 'break-all' }}
                      >
                        {selectedFramework.tc_url}
                      </span>
                      <div style={{
                        fontSize: '0.625rem', fontWeight: 600,
                        color: 'var(--slate)', background: 'var(--off-white)',
                        padding: '3px 8px', border: '1px solid var(--silver)',
                        cursor: 'default', flexShrink: 0
                      }}>
                        AI Variance Analysis — Coming Soon
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '8px' }}>Competitors on this Framework</div>
                  {frameworkCompetitors.length === 0 ? (
                    <p style={{ fontSize: '0.875rem', color: 'var(--slate)', margin: 0 }}>No competitors linked. Tag this framework from a competitor profile in Competitive Intelligence.</p>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {frameworkCompetitors.map(cf => (
                        <div key={cf.id} onClick={() => onOpenCompetitor && onOpenCompetitor(cf.competitor_id)} style={{ background: 'var(--green-pale)', border: '1px solid var(--green)', padding: '6px 12px', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--green-light)'} onMouseLeave={e => e.currentTarget.style.background = 'var(--green-pale)'}>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--green-dark)' }}>{cf.competitors?.name}</div>
                          {cf.competitors?.size && <div style={{ fontSize: '0.75rem', color: 'var(--slate)' }}>{cf.competitors.size}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedFramework.notes && (
                  <div>
                    <div style={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--green)', marginBottom: '6px' }}>Notes</div>
                    <p style={{ fontSize: '0.875rem', color: 'var(--slate)', fontStyle: 'italic', lineHeight: 1.6, margin: 0 }}>{selectedFramework.notes}</p>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleToggleActive(selectedFramework)}
                  className="btn btn--secondary"
                  style={{ fontSize: '0.8125rem' }}
                >
                  {selectedFramework.is_active ? 'Mark Inactive' : 'Mark Active'}
                </button>
                <button
                  onClick={() => handleDelete(selectedFramework.id)}
                  style={{ background: 'none', border: '1px solid var(--silver)', color: 'var(--slate)', padding: '8px 16px', fontSize: '0.8125rem', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                >
                  Delete Framework
                </button>
              </div>
            </div>
          )}

          {!selectedFramework && !showAdd && (
            <div className="card">
              <p style={{ color: 'var(--slate)', textAlign: 'center', padding: '32px 0' }}>
                Select a framework from the list or click <strong>+ Add</strong> to create your first one.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="actions" style={{ marginTop: '24px' }}>
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>← Dashboard</button>
      </div>
    </div>
  )
}

export default FrameworkLibrary